import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ArrowLeft, Plus, Users, MapPin, Phone, EnvelopeSimple,
  Pencil, Trash, MagnifyingGlass, CheckCircle, PauseCircle,
  Flag, Ruler, SpinnerGap, NotePencil, Tag
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { SEO } from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', icon: CheckCircle },
  paused: { label: 'Paused', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400', icon: PauseCircle },
  completed: { label: 'Completed', color: 'bg-muted text-muted-foreground', icon: Flag },
};

const emptyForm = {
  client_name: '',
  client_email: '',
  client_phone: '',
  address: '',
  service_type: '',
  status: 'active',
  notes: '',
  tags_input: '',
  last_service_date: '',
};

const ClientFormDialog = ({ open, onClose, onSaved, editTarget }) => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (editTarget) {
        setForm({
          client_name: editTarget.client_name || '',
          client_email: editTarget.client_email || '',
          client_phone: editTarget.client_phone || '',
          address: editTarget.address || '',
          service_type: editTarget.service_type || '',
          status: editTarget.status || 'active',
          notes: editTarget.notes || '',
          tags_input: (editTarget.tags || []).join(', '),
          last_service_date: editTarget.last_service_date || '',
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, editTarget]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.client_name.trim() || !form.address.trim()) {
      toast.error('Client name and address are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        address: form.address,
        service_type: form.service_type,
        status: form.status,
        notes: form.notes,
        tags: form.tags_input.split(',').map((t) => t.trim()).filter(Boolean),
        last_service_date: form.last_service_date || null,
      };
      const url = editTarget
        ? `${API_URL}/api/client-properties/${editTarget.id}`
        : `${API_URL}/api/client-properties`;
      const method = editTarget ? 'put' : 'post';
      const res = await axios[method](url, payload, { withCredentials: true });
      toast.success(editTarget ? 'Client updated' : 'Client added');
      onSaved(res.data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Could not save client';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">
            {editTarget ? 'Edit Client Property' : 'Add Client Property'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Client name *</Label>
            <Input
              value={form.client_name}
              onChange={(e) => update('client_name', e.target.value)}
              placeholder="e.g., John Smith"
              data-testid="client-name-input"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => update('client_email', e.target.value)}
                placeholder="client@example.com"
                data-testid="client-email-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input
                value={form.client_phone}
                onChange={(e) => update('client_phone', e.target.value)}
                placeholder="(555) 123-4567"
                data-testid="client-phone-input"
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Property address *</Label>
            <Input
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder="123 Main St, Round Rock, TX"
              data-testid="client-address-input"
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Service type</Label>
              <Input
                value={form.service_type}
                onChange={(e) => update('service_type', e.target.value)}
                placeholder="Leveling, mowing, fertilization…"
                data-testid="client-service-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => update('status', v)}>
                <SelectTrigger className="mt-1" data-testid="client-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Tags (comma-separated)</Label>
            <Input
              value={form.tags_input}
              onChange={(e) => update('tags_input', e.target.value)}
              placeholder="leveling, fall, repeat"
              data-testid="client-tags-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Last service date</Label>
            <Input
              type="date"
              value={form.last_service_date}
              onChange={(e) => update('last_service_date', e.target.value)}
              data-testid="client-last-service-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <textarea
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              placeholder="Gate code, dog warnings, sprinkler notes…"
              rows={3}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="client-notes-input"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose} disabled={submitting} data-testid="client-cancel-btn">
              Cancel
            </Button>
            <Button className="flex-1" onClick={handleSubmit} disabled={submitting} data-testid="client-save-btn">
              {submitting ? <SpinnerGap size={16} className="mr-1 animate-spin" /> : null}
              {editTarget ? 'Save changes' : 'Add client'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ClientCard = ({ client, onEdit, onDelete, onOpenMeasure }) => {
  const statusCfg = STATUS_CONFIG[client.status] || STATUS_CONFIG.active;
  const StatusIcon = statusCfg.icon;
  return (
    <Card className="border border-border/60 hover:border-primary/40 transition-colors" data-testid={`client-card-${client.id}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-bold truncate">{client.client_name}</h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${statusCfg.color}`}>
                <StatusIcon size={10} weight="fill" /> {statusCfg.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground flex items-start gap-1 mt-0.5">
              <MapPin size={11} weight="fill" className="mt-0.5 shrink-0" />
              <span className="truncate">{client.address}</span>
            </p>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenMeasure(client)} data-testid={`client-measure-${client.id}`} title="Open Property Measure">
              <Ruler size={16} />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(client)} data-testid={`client-edit-${client.id}`} title="Edit">
              <Pencil size={16} />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => onDelete(client)} data-testid={`client-delete-${client.id}`} title="Delete">
              <Trash size={16} />
            </Button>
          </div>
        </div>

        {/* Contact + service */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          {client.client_email && (
            <a href={`mailto:${client.client_email}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-primary">
              <EnvelopeSimple size={12} /> <span className="truncate">{client.client_email}</span>
            </a>
          )}
          {client.client_phone && (
            <a href={`tel:${client.client_phone}`} className="flex items-center gap-1.5 text-muted-foreground hover:text-primary">
              <Phone size={12} /> {client.client_phone}
            </a>
          )}
          {client.service_type && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <NotePencil size={12} /> {client.service_type}
            </span>
          )}
          {client.last_service_date && (
            <span className="text-muted-foreground">
              Last serviced: {new Date(client.last_service_date).toLocaleDateString()}
            </span>
          )}
        </div>

        {/* Tags */}
        {client.tags?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {client.tags.map((t) => (
              <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground flex items-center gap-1">
                <Tag size={9} /> {t}
              </span>
            ))}
          </div>
        )}

        {/* Notes */}
        {client.notes && (
          <p className="mt-3 text-xs text-muted-foreground italic border-l-2 border-border pl-3">
            {client.notes}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const ClientsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const isPro = user?.role === 'admin' || user?.subscription_tier === 'pro_business';

  const fetchClients = useCallback(async () => {
    if (!isPro) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API_URL}/api/client-properties`, { withCredentials: true });
      setClients(res.data.clients || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status !== 403) {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Could not load clients');
      }
    } finally {
      setLoading(false);
    }
  }, [isPro]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleSaved = (saved) => {
    setClients((prev) => {
      const exists = prev.find((c) => c.id === saved.id);
      return exists ? prev.map((c) => (c.id === saved.id ? saved : c)) : [saved, ...prev];
    });
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`Remove "${client.client_name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/client-properties/${client.id}`, { withCredentials: true });
      setClients((prev) => prev.filter((c) => c.id !== client.id));
      toast.success('Client removed');
    } catch {
      toast.error('Could not remove client');
    }
  };

  const handleOpenMeasure = (client) => {
    // Jump over to Property Measure with the address pre-filled via query param
    navigate(`/measure?address=${encodeURIComponent(client.address)}&client=${encodeURIComponent(client.client_name)}`);
  };

  const filtered = clients.filter((c) => {
    const matchesSearch = !search
      || c.client_name?.toLowerCase().includes(search.toLowerCase())
      || c.address?.toLowerCase().includes(search.toLowerCase())
      || c.client_email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Gate: non-Pro users get the upsell prompt instead of data
  if (!isPro && !loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="clients-gate">
        <Card className="max-w-md border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-transparent to-accent/5">
          <CardContent className="p-8 text-center space-y-4">
            <Users size={40} weight="duotone" className="mx-auto text-primary" />
            <h2 className="text-2xl font-bold">Client Properties is a Pro tool</h2>
            <p className="text-sm text-muted-foreground">
              Manage every customer, every address, every job history — in one place, right next to your measuring and quoting tools.
            </p>
            <Button onClick={() => navigate('/pro')} className="w-full" data-testid="clients-pro-cta">
              See what Pro unlocks →
            </Button>
            <Button variant="ghost" onClick={() => navigate(-1)} className="w-full" data-testid="clients-back-btn">
              Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="clients-page">
      <SEO title="Client Properties — TurfPulse Pro" description="Manage your lawn care clients, properties, and service history in one place." />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="clients-header-back" className="h-8 w-8">
              <ArrowLeft size={18} />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <TurfPulseLogoSVG size={26} />
              <span className="text-sm font-semibold">TurfPulse <span className="text-primary">Pro</span></span>
            </Link>
          </div>
          <Button size="sm" onClick={() => { setEditTarget(null); setShowDialog(true); }} className="gap-1" data-testid="clients-add-btn">
            <Plus size={14} weight="bold" /> Add client
          </Button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Client Properties</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every customer, every address, every service note — organized so you can walk into any job prepared.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, address, email…"
              className="pl-9"
              data-testid="clients-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-40" data-testid="clients-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
            <SpinnerGap size={18} className="animate-spin mr-2" /> Loading clients…
          </div>
        ) : clients.length === 0 ? (
          <Card className="border-dashed border-2 border-border/60">
            <CardContent className="p-10 text-center space-y-3">
              <Users size={36} weight="duotone" className="mx-auto text-muted-foreground" />
              <h3 className="text-lg font-semibold">No client properties yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Add your first client and you&apos;ll have their address, contact, and service history one tap away — forever.
              </p>
              <Button onClick={() => { setEditTarget(null); setShowDialog(true); }} className="gap-1" data-testid="clients-empty-add-btn">
                <Plus size={14} weight="bold" /> Add your first client
              </Button>
            </CardContent>
          </Card>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground">
            No clients match your filters.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filtered.map((c) => (
              <ClientCard
                key={c.id}
                client={c}
                onEdit={(cl) => { setEditTarget(cl); setShowDialog(true); }}
                onDelete={handleDelete}
                onOpenMeasure={handleOpenMeasure}
              />
            ))}
          </div>
        )}

        {/* Footer count */}
        {clients.length > 0 && (
          <p className="text-xs text-muted-foreground text-center mt-8" data-testid="clients-count">
            Showing {filtered.length} of {clients.length} client{clients.length === 1 ? '' : 's'}
          </p>
        )}
      </main>

      <ClientFormDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        onSaved={handleSaved}
        editTarget={editTarget}
      />
    </div>
  );
};

export default ClientsPage;
